import React, { useEffect, memo, useState, useRef, useMemo } from 'react';
import './index.less';
import { ConfigProvider, Modal, Slider, InputNumber, App } from 'antd';
import queryDomMethods from '@/utils/queryDomMethods';
import { useAppStore } from '@/store';
import { getHomePagePredictScoreDetailInfo, saveHomePagePredictScoreDetail } from '@/apis';
import { IHomePagePredictionScoreDetail } from '@/types';
import { useCommonInfoContext } from '@/pages/prediction-score/context/CommonInfoProvider';
import Big from 'big.js';

interface IProps {
    open: boolean;
    setOpen: Function;
    onUpdate: Function;
}

const PredictionScoreDetailModal = (props: IProps) => {
    const { open, setOpen, onUpdate } = props;
    const {
        themeStore: { theme },
    } = useAppStore();
    const {
        commonInfo: { projectId, subjectId, examSeasonId, examSyllabusId },
    } = useCommonInfoContext();
    const { message } = App.useApp();

    const detailDataInitInfo = useRef<IHomePagePredictionScoreDetail>({
        projectId: 0,
        subjectId: 0,
        allowsCustomSettings: 0, // 是否允许自定义设置, 1允许 0 不允许
        goalScoreDesc: '',
        predictionScoreDesc: '',
        totalGoalScore: 0,
        sectionList: [],
    });
    const [detailData, setDetailData] = useState<IHomePagePredictionScoreDetail>(detailDataInitInfo.current);
    const isAllowsCustomSettings = useMemo(() => detailData.allowsCustomSettings === 1, [detailData.allowsCustomSettings]);
    const [scoreMap, setScoreMap] = useState<{ [key: number]: number }>({});
    const [totalGoalScore, setTotalGoalScore] = useState<number>();

    useEffect(() => {
        let total = 0;
        Object.keys(scoreMap).forEach(key => {
            total = new Big(total).plus(scoreMap[key as unknown as number]).toNumber();
        });
        setTotalGoalScore(total);
    }, [scoreMap]);

    const onChange = (newValue: number | null, sectionId: number) => {
        // 确保输入的值是整数
        if (newValue !== null && Number.isInteger(newValue)) {
            setScoreMap(oldValue => {
                return {
                    ...oldValue,
                    [sectionId]: newValue,
                };
            });
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // 允许的键：删除键、退格键、方向键（箭头键）、Enter、Tab
        const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter', 'Tab'];
        if (!/\d/.test(e.key) && !allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const fetchDetail = async () => {
        const response = await getHomePagePredictScoreDetailInfo({
            projectId,
            subjectId,
            examSeasonId,
            examSyllabusId,
        });
        if (response.status === 0 && response.result) {
            setDetailData(response.result);
            setTotalGoalScore(response.result.totalGoalScore);
            const initSourceMap: { [key: number]: number } = {};
            response.result.sectionList.forEach(item => {
                initSourceMap[item.sectionId] = item.goalScore;
            });
            setScoreMap(initSourceMap);
        }
    };

    const isFetchIng = useRef(false);

    const resetScore = () => {
        if (isFetchIng.current) {
            return;
        }
        const resetSourceMap: { [key: number]: number } = {};
        detailData.sectionList.forEach(item => {
            resetSourceMap[item.sectionId] = item.goalScore;
        });
        setScoreMap(resetSourceMap);
    };

    const beforeSave: () => boolean = () => {
        let totalGoalScore = 0;
        for (const id in scoreMap) {
            totalGoalScore += scoreMap[id];
        }
        if (!totalGoalScore) {
            message.error('总目标分必须大于0');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (isFetchIng.current) {
            return;
        }
        if (!beforeSave()) {
            return;
        }
        isFetchIng.current = true;
        const sectionList = [];
        for (const id in scoreMap) {
            sectionList.push({
                goalScore: scoreMap[id],
                sectionId: +id,
            });
        }
        const response = await saveHomePagePredictScoreDetail({
            projectId,
            subjectId,
            examSeasonId,
            examSyllabusId,
            sectionList,
        });
        if (response.status === 0) {
            message.success('更新目标分成功');
            setOpen(false);
            onUpdate();
        }
        isFetchIng.current = false;
    };

    useEffect(() => {
        if (open) {
            fetchDetail();
        }
    }, [open]);

    const targetScoreSlider = (section: IHomePagePredictionScoreDetail['sectionList'][number]) => {
        return (
            <>
                <ConfigProvider
                    theme={{
                        components: {
                            InputNumber: {
                                activeBg: theme.mainColorAlpha?.alpha1,
                                activeBorderColor: theme.mainColorAlpha?.alpha1,
                                hoverBg: theme.mainColorAlpha?.alpha1,
                                hoverBorderColor: theme.mainColorAlpha?.alpha1,
                                controlWidth: 48,
                            },
                            Slider: {
                                railBg: '#E5E5E5',
                                railHoverBg: '#E5E5E5',
                                railSize: 6,
                                trackBg: '#E5E5E5',
                                trackHoverBg: '#E5E5E5',
                            },
                        },
                    }}
                >
                    <InputNumber
                        controls={false}
                        min={0}
                        size="small"
                        max={section.sectionScore}
                        value={scoreMap[section.sectionId]}
                        onChange={newValue => onChange(newValue, section.sectionId)} // 确保输入的值是整数
                        onKeyDown={onKeyDown}
                        disabled={!isAllowsCustomSettings}
                    />
                    <Slider
                        min={0}
                        max={section.sectionScore}
                        onChange={newValue => onChange(newValue, section.sectionId)} // 确保输入的值是整数
                        value={typeof scoreMap[section.sectionId] === 'number' ? scoreMap[section.sectionId] : 0}
                        tooltip={{
                            getPopupContainer: queryDomMethods.querySubTikuRoot,
                            color: 'rgba(51, 51, 51, 0.8)',
                        }}
                        disabled={!isAllowsCustomSettings}
                    />
                </ConfigProvider>
            </>
        );
    };

    const modalFooter = () => (
        <div className="sub-tiku__predict-score-detail-modal-footer">
            <div className="sub-tiku__predict-score-detail-modal-footer-extra">
                <div className="sub-tiku__predict-score-total-score">
                    <p>总目标分:</p>
                    <p>{totalGoalScore}分</p>
                </div>
            </div>
            <div className="sub-tiku__predict-score-detail-modal-footer-btns">
                <div className="sub-tiku__predict-score-detail-modal-footer-btn sub-tiku__tiku-default-btn" onClick={resetScore}>
                    <span>重置</span>
                </div>
                <div
                    className="sub-tiku__predict-score-detail-modal-footer-btn sub-tiku__tiku-default-btn sub-tiku__tiku-plain-btn"
                    onClick={handleSave}
                >
                    <span>保存</span>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Modal
                centered
                width={580}
                className="sub-tiku__predict-score-detail-modal"
                open={open}
                footer={isAllowsCustomSettings ? modalFooter : null}
                closable
                styles={{
                    body: { height: isAllowsCustomSettings ? '442px' : '530px' },
                }}
                title="预测分详情"
                getContainer={queryDomMethods.querySubTikuRoot}
                onCancel={() => setOpen(false)}
            >
                <div className="sub-tiku__predict-score-explain">
                    <div className="sub-tiku__predict-score-explain-title">预测分说明</div>
                    <div className="sub-tiku__predict-score-explain-content">{detailData.predictionScoreDesc}</div>
                </div>
                {isAllowsCustomSettings && (
                    <div className="sub-tiku__predict-score-explain">
                        <div className="sub-tiku__predict-score-explain-title">定制目标分</div>
                        <div className="sub-tiku__predict-score-explain-content">{detailData.goalScoreDesc}</div>
                    </div>
                )}
                {!isAllowsCustomSettings && (
                    <div className="sub-tiku__predict-score-explain">
                        <div className="sub-tiku__predict-score-explain-title">
                            总目标分: <span className="em">{detailData.totalGoalScore}分</span>
                        </div>
                    </div>
                )}
                <div className="sub-tiku__exam-syllabus-list">
                    {detailData.sectionList?.map(item => (
                        <div className="sub-tiku__exam-syllabus-list-item" key={item.sectionId}>
                            <div className="sub-tiku__exam-syllabus-list-item-title">{item.sectionName}</div>
                            <div className="sub-tiku__exam-syllabus-list-item-content">
                                <span>目标分:</span>
                                {targetScoreSlider(item)}
                                <span className="sub-tiku__exam-syllabus-list-item-total-score">总{item.sectionScore}分</span>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>
        </>
    );
};

export default memo(PredictionScoreDetailModal);
